import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ComponentType} from 'react';
import {connect} from 'react-redux';

import {RootState} from 'App/store/rootReducer';
import {
  TArtificialization,
  THarmony,
  TIcConstruction,
  TIntegration,
  TShadows,
  TSunExposure,
  TOpposite,
  TCharacteristics,
  Indicators,
} from 'Libs/map3d/indicators_types';

export type IndicatorsSlice = Indicators & {
  isReady: boolean;
};

const initialState: IndicatorsSlice = {
  artificialization: {
    soilPercentage: null,
    originalSoilPercentage: null,
    artificializationRate: null,
  },
  sunExposure: {
    averageSunExposureRatio: null,
  },
  icConstruction: {
    concrete: null,
    lowCarbonConcrete: null,
    woodAndConcrete: null,
    wood: null,
    exportJson: null,
  },
  integration: {
    blindWallLinear: null,
    integrationRatio: null,
  },
  harmony: {
    footprintRatio: null,
    harmonyRating: null,
    projectMeanArea: null,
    neighborsMeanArea: null,
  },
  shadows: {
    totalFacadesArea: null,
    hadShadowsBeforeProject: false,
    shadowIncreaseSurface: null,
    shadowIncreasePercentage: null,
  },
  opposite: {
    oppositeRatio: null,
  },
  characteristics: {
    buildings: {
      totalSdp: null,
      totalShob: null,
      totalShab: null,
      totalFootprintArea: null,
      totalBaseFacadesLength: null,
      totalFacadesArea: null,
    },
    parkings: {
      totalParkingFootprintArea: null,
      maxParkingLevelCount: null,
      totalParkingSpaces: null,
    },
  },
  isReady: false,
};

const indicatorsSlice = createSlice({
  name: 'indicators',
  initialState,
  reducers: {
    setIndicators: (_, {payload}: PayloadAction<Indicators>) => ({
      ...payload,
      isReady: true,
    }),
    resetIndicators: () => initialState,
  },
});

const mapIndicatorsToProps = (state: RootState): IndicatorsSlice =>
  state.map3DEntities.indicators;

export const connectToIndicators = (
  Component: ComponentType<IndicatorsSlice>,
): ComponentType =>
  connect(mapIndicatorsToProps, null, null, {forwardRef: true})(Component);

export const selectIndicators = (state: RootState): IndicatorsSlice =>
  state.map3DEntities.indicators;
export const selectArtificialization = (state: RootState): TArtificialization =>
  state.map3DEntities.indicators.artificialization;
export const selectSunExposure = (state: RootState): TSunExposure =>
  state.map3DEntities.indicators.sunExposure;
export const selectIcConstruction = (state: RootState): TIcConstruction =>
  state.map3DEntities.indicators.icConstruction;
export const selectIntegration = (state: RootState): TIntegration =>
  state.map3DEntities.indicators.integration;
export const selectHarmony = (state: RootState): THarmony =>
  state.map3DEntities.indicators.harmony;
export const selectShadows = (state: RootState): TShadows =>
  state.map3DEntities.indicators.shadows;
export const selectOpposite = (state: RootState): TOpposite =>
  state.map3DEntities.indicators.opposite;
export const selectCharacteristics = (state: RootState): TCharacteristics =>
  state.map3DEntities.indicators.characteristics;
export const selectIsIndicatorsReady = (state: RootState): boolean =>
  state.map3DEntities.indicators.isReady;

export const {setIndicators, resetIndicators} = indicatorsSlice.actions;

export default indicatorsSlice.reducer;
